import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List as Menu, X, CaretDown } from "@phosphor-icons/react";
import { openBetaModal } from '../lib/beta';
import { MARKETING_HOME, homeSection } from '../lib/marketingHome';

const INK = '#2A1E5C';
const GOLD = '#F5CB5C';

const MENU_ITEMS = [
  { to: '/about', label: 'About' },
  { to: '/method', label: 'The Method' },
  { to: '/learn', label: 'Help Guides' },
  { to: homeSection('season-pass'), label: 'Pricing' },
  { to: '/whats-new', label: "What's New" },
  { to: '/support', label: 'Ask Caddy 🏁' },
];

// Already on the homepage? Scroll instead of re-navigating to the same hash.
const usePricingClick = () => {
  const { pathname } = useLocation();
  return (e) => {
    if (pathname !== MARKETING_HOME) return;
    e.preventDefault();
    document.getElementById('season-pass')?.scrollIntoView({ behavior: 'smooth' });
  };
};

const useMember = () => {
  const [member, setMember] = useState(false);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMember(!!(d && d.email)))
      .catch(() => {});
  }, []);
  return member;
};

export const MobileSiteMenu = () => {
  const [open, setOpen] = useState(false);
  const member = useMember();
  const onPricingClick = usePricingClick();
  return (
    <div className="flex sm:hidden items-center gap-1.5">
      <Link
        to={member ? '/dashboard' : '/login?next=/dashboard'}
        className="font-body text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm"
        style={{ background: GOLD, color: INK }}
        data-testid="nav-member-login-mobile"
      >
        {member ? 'Dashboard' : 'Login'}
      </Link>
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="p-2 rounded-lg"
          style={{ color: INK }}
          aria-label={open ? 'Close menu' : 'Open menu'}
          data-testid="nav-mobile-menu-btn"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        {open && (
          <div
            className="absolute right-0 top-full mt-2 w-60 rounded-2xl shadow-lg py-2 px-1 z-50"
            style={{ background: '#FFFFFF', border: '1px solid rgba(42, 30, 92, 0.1)' }}
            data-testid="nav-mobile-menu"
          >
            {MENU_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={(e) => {
                  setOpen(false);
                  if (item.to === homeSection('season-pass')) onPricingClick(e);
                }}
                className="block px-4 py-3 rounded-lg font-body text-base transition-colors hover:bg-gray-50"
                style={{ color: INK }}
                data-testid={`nav-mobile${item.to.replace(/[/#]/g, '-')}`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/start/free-sprint"
              onClick={() => setOpen(false)}
              className="block mx-3 mt-2 mb-1 px-4 py-2.5 rounded-full font-body text-sm font-bold text-center"
              style={{ background: GOLD, color: INK }}
              data-testid="nav-mobile-start-relay"
            >
              Start Free Sprint →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

/** "Learn", with The Method and Help Guides hanging underneath it. */
const MethodMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="nav-method-menu"
    >
      <Link
        to="/method"
        className="text-sm font-medium hover:opacity-70 inline-flex items-center gap-1"
        style={{ color: INK }}
        onFocus={() => setOpen(true)}
        data-testid="nav-method"
      >
        Learn <CaretDown className="w-3 h-3" />
      </Link>
      {open && (
        <div
          className="absolute left-0 top-full pt-3 z-50"
          data-testid="nav-method-dropdown"
        >
          <div
            className="w-56 rounded-2xl shadow-lg py-2 px-1"
            style={{ background: '#FFFFFF', border: '1px solid rgba(42, 30, 92, 0.1)' }}
          >
            <Link
              to="/method"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 rounded-lg text-sm transition-colors hover:bg-gray-50"
              style={{ color: INK }}
              data-testid="nav-method-overview"
            >
              The Method
            </Link>
            <Link
              to="/learn"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 rounded-lg text-sm transition-colors hover:bg-gray-50"
              style={{ color: INK }}
              data-testid="nav-learn"
            >
              Help Guides
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

/** The one public site nav. `onBetaClick` opens the beta modal (homepage only). */
export const SiteNav = ({ onBetaClick, betaFull = false }) => {
  const member = useMember();
  const onPricingClick = usePricingClick();
  const pillClass = 'px-1.5 py-[1px] sm:py-0.5 rounded-full text-[7px] sm:text-[8px] font-bold tracking-widest leading-none transition-all hover:scale-105 shadow-sm flex-shrink-0 -mt-1 sm:mt-0';
  return (
    <header
      className="sticky top-0 z-40 px-4 sm:px-6 lg:px-8 py-6"
      style={{
        background: 'rgba(253, 251, 247, 0.97)',
        backdropFilter: 'blur(8px)',
        borderBottom: '0.5px solid rgba(42, 30, 92, 0.10)',
        boxShadow: '0 1px 12px -4px rgba(42, 30, 92, 0.06)',
      }}
      data-testid="site-nav"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex flex-col items-start gap-0.5 sm:flex-row sm:items-center sm:gap-2.5">
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="header-brand">
              <img src="/images/paydayrelaylogo-tight.png" alt="Payday Relay" className="h-[45px] sm:h-[57px] w-auto" />
            </Link>
            <button onClick={onBetaClick || openBetaModal} className={pillClass} style={{ background: GOLD, color: INK }} data-testid="beta-open-pill">
              {betaFull ? 'BETA FULL' : 'BETA'}
            </button>
          </div>
          <div className="hidden sm:flex items-center gap-6 ml-6">
            <Link to="/about" className="text-sm font-medium hover:opacity-70" style={{ color: INK }} data-testid="nav-about">
              About
            </Link>
            <MethodMenu />
            <Link
              to={homeSection('season-pass')}
              onClick={onPricingClick}
              className="text-sm font-medium hover:opacity-70"
              style={{ color: INK }}
              data-testid="nav-pricing"
            >
              Pricing
            </Link>
            <Link to="/whats-new" className="text-sm font-medium hover:opacity-70 whitespace-nowrap" style={{ color: INK }} data-testid="nav-whats-new">
              What's New
            </Link>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-6">
          <Link
            to={member ? '/dashboard' : '/login?next=/dashboard'}
            className="text-sm font-medium hover:opacity-70 whitespace-nowrap"
            style={{ color: INK }}
            data-testid="nav-member-login"
          >
            {member ? 'Dashboard' : 'Login'}
          </Link>
          <Link
            to="/start/free-sprint"
            className="text-sm font-bold px-5 py-2.5 rounded-full transition-transform hover:scale-105 whitespace-nowrap shadow-sm"
            style={{ background: GOLD, color: INK }}
            data-testid="nav-start-sprint"
          >
            Start Your Free Sprint
          </Link>
        </div>

        <MobileSiteMenu />
      </div>
    </header>
  );
};
